import React, { useEffect, useState, useContext, useRef } from 'react';
import SocketContext from '../../context/SocketContext';
import GameRoomContext from '../../context/GameRoomContext';
import useGameBase from '../../hooks/useGameBase';
import Modal from '../common/Modal';
// UserContext previously used for dark-mode; not required in this component

// Utility: load YouTube IFrame API once and return a promise that resolves when ready
function loadYouTubeApi() {
  // Reuse a global promise if already loading/loaded
  if (window.__ytApiPromise) return window.__ytApiPromise;

  window.__ytApiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) return resolve(window.YT);

    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = function () {
      if (typeof previous === 'function') previous();
      resolve(window.YT);
    };

    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    document.head.appendChild(script);
  });

  return window.__ytApiPromise;
}

const DEFAULT_VIDEO_ID = 'nLpm4aysr8I';

// Curated list of YouTube IDs used for the "random" button. Add/remove IDs as desired.
const RANDOM_VIDEO_IDS = [
  'dQw4w9WgXcQ', // Rick Astley - Never Gonna Give You Up (classic test)
  'M7lc1UVf-VE', // YouTube API demo
  'ysz5S6PUM-U', // Sample video
  '3JZ_D3ELwOQ', // Example music
  'e-ORhEE9VVg', // Example
];

// (short URL helper removed - random button now directly suggests the ID)

// Helper: extract video id from YouTube url or id (client-side)
function extractYouTubeId(input) {
  if (!input) return null;
  const s = String(input).trim();
  let m = s.match(/(?:youtu\.be\/)([A-Za-z0-9_-]{11})/);
  if (m) return m[1];
  m = s.match(/[?&]v=([A-Za-z0-9_-]{11})/);
  if (m) return m[1];
  m = s.match(/(?:embed\/)([A-Za-z0-9_-]{11})/);
  if (m) return m[1];
  if (/^[A-Za-z0-9_-]{11}$/.test(s)) return s;
  m = s.match(/\/([A-Za-z0-9_-]{11})(?:$|[?&])/);
  if (m) return m[1];
  return null;
}

const YouTubeGame = ({ gameState = {}, roomId, onGameAction, socket }) => {
  const [volume, setVolume] = useState(1);
  const socketCtx = useContext(SocketContext);
  const { gameRoomData } = useContext(GameRoomContext);
  const { submitAction } = useGameBase({ gameState, gamePlayers: gameState.players || [], user: null, socket: socketCtx, roomId });

  const [videoIdDisplay, setVideoIdDisplay] = useState(gameState.videoId ?? DEFAULT_VIDEO_ID);
  const [currentTimeDisplay, setCurrentTimeDisplay] = useState(gameState.currentTime || 0);
  const [, setPlayingDisplay] = useState(!!gameState.playing);
  // endscreen overlay removed: no local overlay state required

  const playerRef = useRef(null); // YT.Player instance
  const playerContainerRef = useRef(null);
  const pollingRef = useRef(null);
  const lastServerUpdateAtRef = useRef(gameState.lastUpdateAt || 0);
  const lastProcessedServerUpdateRef = useRef(null);
  const lastServerVideoIdRef = useRef(gameState.videoId || '');
  const lastQueueKeyRef = useRef(JSON.stringify(gameState.queue || []));
  const endedTriggerRef = useRef(0);

  // Compute host boolean
  const isHost = socket?.id && (socket.id === (gameRoomData?.gameRoomDetails?.hostSocketId || socket.id));

  // Helper to emit game actions using provided handler or socket fallback
  const emitAction = (type, payload) => {
    if (typeof onGameAction === 'function') {
      onGameAction({ type, payload });
      return;
    }
    if (submitAction) {
      submitAction({ type, payload }).catch(()=>{});
      return;
    }
    if (!socket) return;
    socket.emit('gameAction', { roomId, action: { type, payload } });
  };

  // Create player when we have a container and the API is ready
  useEffect(() => {
    let mounted = true;

    async function createPlayer() {
      const YT = await loadYouTubeApi();
      if (!mounted) return;

      // If player already exists, don't recreate
      if (playerRef.current) return;

      const playerVars = {
        // Important: enable JS API so we can control the player
        enablejsapi: 1,
        origin: window.location.origin,
        // Disable related videos where possible (rel=0), modest branding, hide annotations
        iv_load_policy: 3,
        playsinline: 1,
        autoplay: 1,
      };
      // Hosts should be able to control playback; viewers should not.
      if (isHost) {
        // give host full controls
        playerVars.controls = 1;
        playerVars.disableKb = 0;
        playerVars.rel= 1;
      } else {
        // viewers: hide controls and disable keyboard to prevent local control
        playerVars.controls = 0;
        playerVars.disableKb = 1;
        playerVars.rel= 0;
      }

      playerRef.current = new YT.Player(playerContainerRef.current, {
        height: '360',
        width: '100%',
        playerVars: playerVars,
        events: {
          onReady: () => {
            // Attempt to sync to current server state when ready (fall back to default video)
            try {
              const p = playerRef.current;
              if (!p) return;
              const idToLoad = gameState.videoId ?? DEFAULT_VIDEO_ID;
              if (idToLoad) {
                if (gameState.playing) {
                  // loadVideoById typically begins playback; call playVideo to increase chance of starting
                  p.loadVideoById(idToLoad, gameState.currentTime || 0);
                  try { p.playVideo(); } catch (e) { /* autoplay may be blocked */ }
                } else {
                  p.cueVideoById(idToLoad, gameState.currentTime || 0);
                }
              }
              // Set initial volume
              p.setVolume(volume * 100);
            } catch (err) {
              // ignore
            }
          },

          onStateChange: (e) => {
            // Local player state change -> if host, emit actions to server
            const p = playerRef.current;
            if (!p) return;
            const state = e.data;
            const YT = window.YT;
            // YT.PlayerState.PLAYING === 1, PAUSED === 2, ENDED === 0
            if (isHost) {
              try {
                const now = p.getCurrentTime();
                if (state === YT.PlayerState.PLAYING) {
                  emitAction('play', { currentTime: now });
                } else if (state === YT.PlayerState.PAUSED) {
                  emitAction('pause', { currentTime: now });
                } else if (state === YT.PlayerState.ENDED) {
                  // When host reaches end of current video, advance to next suggestion.
                  try {
                    const nowMs = Date.now();
                    // Debounce to avoid duplicate emits
                    if (nowMs - (endedTriggerRef.current || 0) > 1500) {
                      endedTriggerRef.current = nowMs;
                      emitAction('playNext', {});
                    }
                  } catch (e) {}
                }
              } catch (err) {
                // ignore
              }
            }
            // For all clients: when the video ends, temporarily hide the player's endscreen
            // overlay handling removed
          },
        },
      });
    }

    createPlayer();

    return () => {
      mounted = false;
      if (playerRef.current && playerRef.current.destroy) {
        try { playerRef.current.destroy(); } catch (e) {}
        playerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerContainerRef.current, isHost]);

  // Update player volume when slider changes
  useEffect(() => {
    const p = playerRef.current;
    if (p && typeof p.setVolume === 'function') {
      p.setVolume(volume * 100);
    }
  }, [volume]);

  // Poll player for current time to update the UI while playing
  useEffect(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    function startPolling() {
      if (pollingRef.current) return;
      pollingRef.current = setInterval(() => {
        const p = playerRef.current;
        if (!p || typeof p.getCurrentTime !== 'function') return;
        try {
          const t = p.getCurrentTime();
          setCurrentTimeDisplay(t);
          const st = (p.getPlayerState && p.getPlayerState() === window.YT.PlayerState.PLAYING);
          setPlayingDisplay(!!st);
        } catch (err) {
          // ignore
        }
      }, 500);
    }

    // Start polling if player exists
    if (playerRef.current) startPolling();

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, []);

  // Apply authoritative server state with drift correction
  useEffect(() => {
    // Avoid acting on identical server update more than once
    const serverUpdateId = gameState.lastUpdateAt || 0;
    if (!serverUpdateId) {
      // Update display values conservatively; fall back to default video id when none provided
      setVideoIdDisplay(gameState.videoId ?? DEFAULT_VIDEO_ID);
      setPlayingDisplay(!!gameState.playing);
      setCurrentTimeDisplay(gameState.currentTime || 0);
      return;
    }
    if (lastProcessedServerUpdateRef.current === serverUpdateId) return;
    lastProcessedServerUpdateRef.current = serverUpdateId;

    setVideoIdDisplay(gameState.videoId || '');
    setPlayingDisplay(!!gameState.playing);

    

    // If the server only changed the queue (shuffle/reorder) but the currently playing video didn't change,
    // don't reload/seek the player — this prevents interrupting playback on queue-only updates.
    try {
      const newQueueKey = JSON.stringify(gameState.queue || []);
      const effectiveServerVideoId = gameState.videoId ?? DEFAULT_VIDEO_ID;
      if (lastServerVideoIdRef.current === effectiveServerVideoId && newQueueKey !== lastQueueKeyRef.current) {
        // update cached queue key and skip heavy sync logic
        lastQueueKeyRef.current = newQueueKey;
        // still update current time display conservatively
        setCurrentTimeDisplay(gameState.currentTime || 0);
        lastServerUpdateAtRef.current = typeof gameState.lastUpdateAt === 'number' ? gameState.lastUpdateAt : Date.now();
        return;
      }
      // otherwise fall through to normal authoritative sync
      lastQueueKeyRef.current = newQueueKey;
    } catch (e) {}

    const p = playerRef.current;
    if (!p) return;

    const serverNowMs = Date.now();
    // Assume server lastUpdateAt is in milliseconds; if small value, fallback
    const lastUpdateAtMs = typeof gameState.lastUpdateAt === 'number' && gameState.lastUpdateAt > 1000
      ? gameState.lastUpdateAt
      : serverNowMs;

    const elapsedSeconds = Math.max(0, (serverNowMs - lastUpdateAtMs) / 1000);
    const serverPlaybackPos = (gameState.currentTime || 0) + (gameState.playing ? elapsedSeconds : 0);

    // compute local time
    try {
      const localPos = p.getCurrentTime();
      const drift = localPos - serverPlaybackPos;
      const driftThreshold = 0.7; // seconds
      // If different videoId, load new id
      const effectiveServerVideoId = gameState.videoId ?? DEFAULT_VIDEO_ID;
      // Only load/cue the player if the server's video id actually changed from last processed id.
      if (lastServerVideoIdRef.current !== effectiveServerVideoId) {
        // remember we've handled this server video id change
        lastServerVideoIdRef.current = effectiveServerVideoId;
        // load or cue based on playing
        if (gameState.playing) {
          try { p.loadVideoById(effectiveServerVideoId, serverPlaybackPos); } catch (e) {}
        } else {
          try { p.cueVideoById(effectiveServerVideoId, serverPlaybackPos); } catch (e) {}
        }
        
      } else if (Math.abs(drift) > driftThreshold) {
        // seek to authoritative position
        p.seekTo(Math.max(0, serverPlaybackPos), true);
      }

      // Ensure play/pause match server
      const YT = window.YT;
      const localState = p.getPlayerState && p.getPlayerState();
      if (gameState.playing && localState !== YT.PlayerState.PLAYING) {
        try { p.playVideo(); } catch (e) {}
        
      } else if (!gameState.playing && localState === YT.PlayerState.PLAYING) {
        try { p.pauseVideo(); } catch (e) {}
      }
    } catch (err) {
      // ignore; player might not be ready
    }

    // Update UI displays
    setCurrentTimeDisplay(serverPlaybackPos);
    lastServerUpdateAtRef.current = lastUpdateAtMs;
  }, [gameState.lastUpdateAt, gameState.currentTime, gameState.playing, gameState.videoId, gameState.queue]);

  // Host action handlers: operate locally (optimistic) and emit server action
  // onSetVideo handler removed (UI simplified)

  const onSeek = (t) => {
    try {
      const p = playerRef.current;
      if (p && p.seekTo) p.seekTo(t, true);
    } catch (err) {}
    emitAction('seek', { time: t });
  };

  const onPlay = () => {
    try { const p = playerRef.current; if (p && p.playVideo) p.playVideo(); } catch (e) {}
    const now = (playerRef.current && typeof playerRef.current.getCurrentTime === 'function') ? playerRef.current.getCurrentTime() : currentTimeDisplay;
    emitAction('play', { currentTime: now });
  };

  const onPause = () => {
    try { const p = playerRef.current; if (p && p.pauseVideo) p.pauseVideo(); } catch (e) {}
    const now = (playerRef.current && typeof playerRef.current.getCurrentTime === 'function') ? playerRef.current.getCurrentTime() : currentTimeDisplay;
    emitAction('pause', { currentTime: now });
  };

  const onPlayNext = () => {
    // Host-triggered advance to the next queued suggestion
    try { emitAction('playNext', {}); } catch (e) {}
  };

  // Suggestion queue client handlers
  const [suggestInput, setSuggestInput] = useState('');
  const [dragIndex, setDragIndex] = useState(null);
  const [metadataMap, setMetadataMap] = useState({}); // videoId -> { title, author_name, thumbnail_url }
  const [animatingIndex, setAnimatingIndex] = useState(null);
  const [animatePlaying, setAnimatePlaying] = useState(false);
  const [currentSuggestedBy, setCurrentSuggestedBy] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const firstServerVideoProcessedRef = useRef(false);

  const openYouTube = () => {
    
      window.open(`https://www.youtube.com/`, '_blank');
    
  };

  const suggestVideo = () => {
    const raw = (suggestInput || '').trim();
    if (!raw) return;
    const vid = extractYouTubeId(raw);
    if (!vid) {
      try { setModalMessage('Please enter a valid YouTube video id or URL'); setModalOpen(true); } catch (e) {}
      return;
    }
    // Client-side quick duplicate check against authoritative server state
    try {
      const serverQ = Array.isArray(gameState.queue) ? gameState.queue : [];
      const alreadyQueued = serverQ.some(q => q && q.videoId === vid);
      const alreadyPlaying = (gameState.videoId || '') === vid;
      if (alreadyQueued || alreadyPlaying) {
        setModalMessage(alreadyPlaying ? 'This video is already playing' : 'This video is already in the queue');
        setModalOpen(true);
        return;
      }
    } catch (e) {}
    (async () => {
      // Try to fetch metadata (oEmbed) for title/thumbnail; only emit if oEmbed succeeds
      let title = null;
      try {
        const res = await fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent('https://www.youtube.com/watch?v=' + vid)}&format=json`);
        if (res.ok) {
          const j = await res.json();
          title = j.title || null;
          setMetadataMap(m => ({ ...m, [vid]: { title: j.title || null, author_name: j.author_name || null, thumbnail_url: j.thumbnail_url || null } }));
          // Emit suggestion to server (UI will reflect server queue)
          emitAction('suggest', { videoId: vid, userId: socket.userId, username: socket.username, title });
          setSuggestInput('');
          return;
        }
      } catch (e) {
        // network error
      }
  try { setModalMessage('Could not verify the YouTube video — please check the id/URL'); setModalOpen(true); } catch (e) {}
    })();
  };

  // Pick a random curated YouTube ID and immediately suggest it (auto-add to queue).
  const pickRandomVideo = async () => {
    // Attempt to call server endpoint which fetches a random YouTube video dynamically.
    try {
      if (socket && typeof socket.emit === 'function') {
        // socket.emit with acknowledgement callback
        await new Promise((resolve) => {
          socket.emit('getRandomYouTube', { region: 'US' }, (j) => {
            try {
              if (j && j.videoId) {
                const id = j.videoId;
                // Simple duplicate check
                try {
                  const serverQ = Array.isArray(gameState.queue) ? gameState.queue : [];
                  const alreadyQueued = serverQ.some(q => q && q.videoId === id);
                  const alreadyPlaying = (gameState.videoId || '') === id;
                  if (alreadyQueued || alreadyPlaying) {
                    setModalMessage(alreadyPlaying ? 'This video is already playing' : 'This video is already in the queue');
                    setModalOpen(true);
                    resolve();
                    return;
                  }
                } catch (e) {}

                if (j.title) setMetadataMap(m => ({ ...m, [id]: { title: j.title, thumbnail_url: j.thumbnails?.high?.url || j.thumbnails?.default?.url || null } }));
                emitAction('suggest', { videoId: id, userId: socket.userId, username: socket.username, title: j.title || null });
                setSuggestInput('');
                resolve();
                return;
              }
            } catch (e) {}
            resolve();
          });
        });
        return;
      }
    } catch (err) {
      // socket path failed, fall through to curated list
    }

    // Fallback: pick from curated list if server endpoint is unavailable
    try {
      if (!Array.isArray(RANDOM_VIDEO_IDS) || RANDOM_VIDEO_IDS.length === 0) return;
      const id = RANDOM_VIDEO_IDS[Math.floor(Math.random() * RANDOM_VIDEO_IDS.length)];
      // Client-side duplicate check
      try {
        const serverQ = Array.isArray(gameState.queue) ? gameState.queue : [];
        const alreadyQueued = serverQ.some(q => q && q.videoId === id);
        const alreadyPlaying = (gameState.videoId || '') === id;
        if (alreadyQueued || alreadyPlaying) {
          setModalMessage(alreadyPlaying ? 'This video is already playing' : 'This video is already in the queue');
          setModalOpen(true);
          return;
        }
      } catch (e) {}
      // attempt oEmbed quickly
      let title = null;
      try {
        const res = await fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent('https://www.youtube.com/watch?v=' + id)}&format=json`);
        if (res.ok) {
          const j = await res.json();
          title = j.title || null;
          setMetadataMap(m => ({ ...m, [id]: { title: j.title || null, author_name: j.author_name || null, thumbnail_url: j.thumbnail_url || null } }));
        }
      } catch (e) {}
      emitAction('suggest', { videoId: id, userId: socket.userId, username: socket.username, title });
      setSuggestInput('');
    } catch (e) {}
  };

  const removeSuggestion = (index) => {
    // Remove suggestion by server queue index
    try {
      const serverQ = Array.isArray(gameState.queue) ? gameState.queue : [];
      if (index == null || index < 0 || index >= serverQ.length) return;
      emitAction('removeSuggestion', { index });
    } catch (e) {}
  };

  // playNext is emitted by host on ENDED via emitAction in onStateChange; no local wrapper needed

  const playNow = (index) => {
    // Map merged index to server index; do not act if item is pending (avoid interfering with current playback)
    try {
  const serverQ = Array.isArray(gameState.queue) ? gameState.queue : [];
  if (index == null || index < 0 || index >= serverQ.length) return;
  const item = serverQ[index];
  if (!item) return;
  // Optimistic local load for host only (this will start playback)
  try { if (isHost) { const p = playerRef.current; if (p && p.loadVideoById) p.loadVideoById(item.videoId, 0); } } catch (e) {}
  // Tell server to atomically play this suggestion (set video + remove from queue)
  emitAction('playSuggestion', { index });
    } catch (e) {}
  };

  const onDragStart = (e, idx) => {
    setDragIndex(idx);
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch (e) {}
  };
  const onDragOver = (e, idx) => {
    e.preventDefault();
  };
  const onDrop = (e, idx) => {
    e.preventDefault();
    const from = dragIndex != null ? dragIndex : Number(e.dataTransfer.getData('text/plain'));
    const to = idx;
    if (from === to) return;
    // Build new order array of videoIds
    const q = Array.isArray(gameState.queue) ? [...gameState.queue] : [];
    const item = q.splice(from, 1)[0];
    q.splice(to, 0, item);
    const order = q.map(s => s.videoId);
    emitAction('reorderQueue', { order });
    setDragIndex(null);
  };

  // Backfill titles for queue items that lack them by fetching oEmbed when we see new ids
  useEffect(() => {
    let mounted = true;
  const queueIds = (Array.isArray(gameState.queue) ? gameState.queue.map(s => s.videoId).filter(Boolean) : []);
  const historyIds = (Array.isArray(gameState.history) ? gameState.history.map(h => h.videoId).filter(Boolean) : []);
  const idsToFetch = [...new Set(queueIds.concat(historyIds))].filter(id => !metadataMap[id]);
  // also ensure the currently playing video has metadata on first load
  const currentVid = (gameState.videoId || DEFAULT_VIDEO_ID);
  if (currentVid && !metadataMap[currentVid] && !idsToFetch.includes(currentVid)) idsToFetch.push(currentVid);
    if (idsToFetch.length === 0) return;
    (async () => {
      for (const vid of idsToFetch) {
        try {
          const res = await fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent('https://www.youtube.com/watch?v=' + vid)}&format=json`);
          if (!mounted) return;
          if (res.ok) {
            const j = await res.json();
            setMetadataMap(m => ({ ...m, [vid]: { title: j.title || null, author_name: j.author_name || null, thumbnail_url: j.thumbnail_url || null } }));
          }
        } catch (e) {
          // ignore per-id errors
        }
      }
    })();
    return () => { mounted = false; };
  }, [gameState.queue, metadataMap, gameState.videoId, gameState.history]);

  // Sync authoritative history from server state into local history state
  useEffect(() => {
    try {
      const serverHistory = Array.isArray(gameState.history) ? gameState.history.slice(0, 50) : [];
  setHistory(serverHistory);
  // debug: help diagnose missing history in UI
  try { console.debug('YouTubeGame: received server history', serverHistory); } catch (e) {}
    } catch (e) {}
  }, [gameState.history]);

  // Listen for suggestion ack from server so we can show confirmation / update UI quickly
  useEffect(() => {
    if (!socket) return;
    const onSuggestionDuplicate = (data) => {
      try {
        if (data && data.videoId) {
          const msg = data.reason === 'already_playing' ? 'This video is already playing' : 'This video is already in the queue';
          setModalMessage(msg);
          setModalOpen(true);
        }
      } catch (e) {}
    };
    const onPlaySuggestion = (data) => {
      try {
        // Animate the queue item at the provided index
        if (typeof data.index === 'number') {
          setAnimatingIndex(data.index);
          // clear animation after 900ms
          setTimeout(() => setAnimatingIndex(null), 900);
        }
        // Pulse currently playing box
        setAnimatePlaying(true);
        setTimeout(() => setAnimatePlaying(false), 900);
        // If server provided the played suggestion metadata, record who suggested it
        try {
          if (data && data.played && data.played.suggestedBy) {
            setCurrentSuggestedBy(data.played.suggestedBy);
          }
        } catch (e) {}
  // Server is authoritative for history; we'll rely on gameState.history + backfill metadata
  // but still optimistically show the animation and played suggester above.
      } catch (e) {}
    };
  socket.on('playSuggestion', onPlaySuggestion);
  socket.on('suggestionDuplicate', onSuggestionDuplicate);
  return () => { socket.off('playSuggestion', onPlaySuggestion); };
  }, [socket, roomId]);

  // Load persisted history for this room on mount/room change
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`youtubeHistory:${roomId}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setHistory(parsed);
      }
    } catch (e) {}
  }, [roomId]);

  const addFromHistory = (videoId, title) => {
  // Emit suggestion to server (UI will reflect server queue)
  emitAction('suggest', { videoId, userId: socket.userId, username: socket.username, title });
  };

  // pendingSuggestions removed: server queue is authoritative

  // Keep currentSuggestedBy in sync with authoritative server state when possible
  useEffect(() => {
    // If the currently playing video matches an item in the queue, prefer that suggester
    try {
      const q = Array.isArray(gameState.queue) ? gameState.queue : [];
      const matched = q.find(i => i && i.videoId === gameState.videoId);
      if (matched && matched.suggestedBy) {
        setCurrentSuggestedBy(matched.suggestedBy);
      } else {
        // If video changed and there's no matching queue item, prefer the host on first load,
        // otherwise clear suggester.
        if (!matched) {
          if (!firstServerVideoProcessedRef.current) {
            try {
              const hostSock = gameRoomData?.gameRoomDetails?.hostSocketId;
              const hostUid = gameRoomData?.gameRoomDetails?.hostUserId;
              const hostPlayer = (gameRoomData?.gameRoomPlayers || []).find(p => p && (p.socketId === hostSock || p.userId === hostUid || p.isHost));
              if (hostPlayer) {
                setCurrentSuggestedBy({ socketId: hostPlayer.socketId, userId: hostPlayer.userId, username: hostPlayer.username });
              } else {
                setCurrentSuggestedBy(null);
              }
            } catch (e) { setCurrentSuggestedBy(null); }
          } else {
            setCurrentSuggestedBy(null);
          }
        }
      }
    } catch (e) {}
    firstServerVideoProcessedRef.current = true;
  }, [gameState.videoId, gameState.queue, gameRoomData?.gameRoomDetails?.hostSocketId, gameRoomData?.gameRoomDetails?.hostUserId, gameRoomData?.gameRoomPlayers]);

  
  const serverQ = Array.isArray(gameState.queue) ? gameState.queue : [];
  const mergedQueue = serverQ;
  const queueCount = mergedQueue.length;
  // Prefer authoritative suggester from server queue for the currently playing video.
  // If the queue no longer contains the item (it was popped), fall back to the most recent server history entry
  // (which is written when we play a suggestion) and finally to the transient currentSuggestedBy state.
  const currentPlayingItem = serverQ.find(i => i && i.videoId === (gameState.videoId || ''));
  let currentPlayingSuggester = currentSuggestedBy;
  if (currentPlayingItem && currentPlayingItem.suggestedBy) {
    currentPlayingSuggester = currentPlayingItem.suggestedBy;
  } else if (Array.isArray(gameState.history) && gameState.history.length > 0) {
    const top = gameState.history[0];
    if (top && top.videoId === (gameState.videoId || '') && top.suggestedBy) {
      currentPlayingSuggester = top.suggestedBy;
    }
  }
  try { console.debug('YouTubeGame: currentPlayingSuggester', { currentPlayingItem, currentPlayingSuggester, gameStateVideo: gameState.videoId, queueCount }); } catch (e) {}

  return (
    <>
    <div className={`youtube-game flex flex-col items-center w-full p-3 rounded dark:bg-gray-900 dark:text-gray-100 bg-gray-50 text-gray-900`}>
      <div className="mb-2">
      </div>

      <div className="w-full flex justify-center">
        {videoIdDisplay ? (
          <div className="w-full max-w-3xl bg-black dark:bg-black p-1 rounded">
                <div className="relative">
                  <div ref={playerContainerRef} id={`youtube-player-${roomId}`} />
                  
                </div>
          </div>
        ) : (
          <div className="text-gray-700 dark:text-gray-300">No video selected</div>
        )}
      </div>

<>
      <div className="w-full flex flex-col items-center justify-center">
        <div ref={playerContainerRef} />
        <div className="flex items-center gap-2 mt-2 mb-4">
          <label htmlFor="yt-volume-slider" className="text-sm dark:text-gray-200">Volume:</label>
          <input
            id="yt-volume-slider"
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={volume}
            onChange={e => setVolume(Number(e.target.value))}
            className="w-32"
          />
        </div>
        {/* ...existing code... */}
      </div>
      {/* ...existing code... */}
    </>

      {isHost && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {/* host-only set video input removed */}
          <button
            onClick={() => onSeek(Math.max(0, (playerRef.current && playerRef.current.getCurrentTime ? playerRef.current.getCurrentTime() : currentTimeDisplay) - 10))}
            aria-label="Rewind 10 seconds"
            className="px-3 py-1 rounded bg-gray-200 dark:bg-white-700 text-black dark:text-white"
          >
            <img src="/icons/rewind.svg" alt="Rewind 10s" className="w-5 h-5" />
          </button>
          <button
            onClick={() => onSeek((playerRef.current && playerRef.current.getCurrentTime ? playerRef.current.getCurrentTime() : currentTimeDisplay) + 10)}
            aria-label="Fast forward 10 seconds"
            className="px-3 py-1 rounded bg-gray-200 dark:bg-white-700 text-black dark:text-white"
          >
            <img src="/icons/fastforward.svg" alt="Forward 10s" className="w-5 h-5" />
          </button>
          <button
            onClick={onPlay}
            disabled={!!gameState.playing}
            aria-label="Play"
            className={`px-3 py-1 rounded text-black dark:text-white ${gameState.playing ? 'bg-green-200 dark:bg-green-800 opacity-50 cursor-not-allowed' : 'bg-green-400 dark:bg-green-600'}`}
          >
            <img src="/icons/play.svg" alt="Play" className="w-5 h-5" />
          </button>
          <button
            onClick={onPause}
            disabled={!gameState.playing}
            aria-label="Pause"
            className={`px-3 py-1 rounded text-black dark:text-white ${!gameState.playing ? 'bg-red-200 dark:bg-red-800 opacity-50 cursor-not-allowed' : 'bg-red-400 dark:bg-red-600'}`}
          >
            <img src="/icons/pause.svg" alt="Pause" className="w-5 h-5" />
          </button>
          <button
            onClick={onPlayNext}
            disabled={queueCount === 0}
            aria-label="Play next video"
            className={`px-3 py-1 rounded text-black dark:text-white ${queueCount === 0 ? 'bg-gray-200 dark:bg-gray-800 opacity-50 cursor-not-allowed' : 'bg-yellow-400 dark:bg-yellow-600'}`}
          >
            <img src="/icons/playnext.svg" alt="Next" className="w-5 h-5" />
          </button>
        </div>
      )}
      {/* Host control panel: queue and preview */}
      <div className="w-full max-w-3xl mt-4">
        <div className="bg-white dark:bg-gray-800 p-3 rounded shadow">
          
          
          <div className="flex gap-2 mb-3">
            <input value={suggestInput} onChange={e => setSuggestInput(e.target.value)} placeholder="Paste YouTube ID / URL" className="flex-1 px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
            
            <button onClick={suggestVideo} className="px-3 py-1 bg-blue-600 text-white rounded">Add to Queue</button>
            {/* {isHost && (<button onClick={playNext} className="px-3 py-1 bg-green-500 text-white rounded">Play Next</button>)} */}
          </div>
          <div className="flex gap-2 mb-3">
            <button onClick={openYouTube} title="Open YouTube" aria-label="Open YouTube" className="px-3 py-1 bg-blue-500 text-white rounded flex items-center justify-center">
              Open YouTube
            </button>
            <button onClick={pickRandomVideo} title="Automatically a random trending video from YouTube" aria-label="Random video" className="px-3 py-1 bg-yellow-500 text-black rounded flex items-center justify-center">
              <img src="/icons/bulb.svg" alt="Random" className="w-5 h-5" /> Add Random Video
            </button>
          </div>
          <h4 className="font-semibold mb-2">Video Queue {queueCount > 0 ? `(${queueCount})` : ''}</h4>
          {/* Currently playing */}
          <div className={`mb-3 p-2 border rounded bg-gray-50 dark:bg-gray-900 ${animatePlaying ? 'ring-4 ring-blue-300' : ''}`}>
            <div className="text-sm text-gray-500">Currently playing</div>
            <>
              <div className="font-semibold">{(metadataMap[gameState.videoId] && metadataMap[gameState.videoId].title) ? metadataMap[gameState.videoId].title : (gameState.videoId || 'None')}</div>
              {currentPlayingSuggester && <div className="text-xs text-gray-500">Submitted by: {currentPlayingSuggester.socketId === socket.id ? 'you' : (currentPlayingSuggester.username || 'Anonymous')}</div>}
            </>
          </div>
          <div className="space-y-2">
            {(Array.isArray(gameState.queue) && gameState.queue.length > 0) ? (
              (() => {
                const serverQ = Array.isArray(gameState.queue) ? gameState.queue : [];
                return serverQ.map((s, idx) => (
                  <div key={s.videoId + '-' + idx}
                    draggable={isHost}
                    onDragStart={(e) => onDragStart(e, idx)}
                    onDragOver={(e) => onDragOver(e, idx)}
                    onDrop={(e) => onDrop(e, idx)}
                    className={`flex items-center justify-between p-2 rounded border ${isHost ? 'cursor-grab' : ''} ${animatingIndex === idx ? 'opacity-30 transform -translate-x-4 transition-all duration-700' : ''}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      {(metadataMap[s.videoId] && metadataMap[s.videoId].thumbnail_url) ? (
                        <img src={metadataMap[s.videoId].thumbnail_url} alt={(metadataMap[s.videoId].title || s.videoId)} className="w-12 h-8 object-cover rounded" />
                      ) : (
                        <div className="w-12 h-8 bg-gray-200 flex items-center justify-center text-xs">YT</div>
                      )}
                      <div className="min-w-0">
                        <div className="font-semibold text-sm whitespace-normal break-words">{(metadataMap[s.videoId] && metadataMap[s.videoId].title) ? metadataMap[s.videoId].title : (s.title || s.videoId)}</div>
                        <div className="text-xs text-gray-500">Suggested by: {(s.suggestedBy && s.suggestedBy.socketId === socket.id) ? 'you' : (s.suggestedBy?.username || 'Anonymous')}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isHost && (
                        <button
                          onClick={() => playNow(idx)}
                          aria-label="Play now"
                          className="p-1 bg-blue-500 rounded"
                        >
                          <img src="/icons/play.svg" alt="Play" className="w-4 h-4" />
                        </button>
                      )}
                      {(isHost || (s.suggestedBy && (s.suggestedBy.userId === socket.userId || s.suggestedBy.socketId === socket.id))) && (
                        <button onClick={() => removeSuggestion(idx)} aria-label="Remove suggestion" className="p-1 bg-red-400 rounded">
                          <img src="/icons/trash.svg" alt="Remove" className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ));
              })()
            ) : (
              <div className="text-sm text-gray-500">No videos in queue</div>
            )}
          </div>
          

          
          {/* History section */}
          <div className="mt-3 border-t pt-3">
            <button onClick={() => setShowHistory(s => !s)} className="text-sm text-blue-600 dark:text-blue-400">{showHistory ? 'Hide' : 'Show'} previously played</button>
            {showHistory && (
              <div className="mt-2 space-y-2">
                {(history && history.length > 0) ? (
                  history.map((h, i) => (
                    <div key={(h.videoId || '') + '-' + i} className="flex items-center justify-between p-2 rounded border bg-gray-50 dark:bg-gray-900">
                      <div>
                          <div className="font-semibold text-sm">{(metadataMap[h.videoId] && metadataMap[h.videoId].title) ? metadataMap[h.videoId].title : (h.title || h.videoId)}</div>
                          <div className="text-xs text-gray-500">Requested by: {(h.suggestedBy && h.suggestedBy.socketId === socket.id) ? 'you' : (h.suggestedBy?.username || 'Anonymous')}</div>
                          <div className="text-xs text-gray-500">Played at: {new Date(h.playedAt || Date.now()).toLocaleString()}</div>
                        </div>
                      <div className="flex-shrink-0">
                        <button onClick={() => addFromHistory(h.videoId, h.title)} aria-label="Add to queue" className="p-1 bg-blue-600 text-white rounded">
                          <img src="/icons/add.svg" alt="Add" className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-gray-500">No history yet</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  <Modal show={modalOpen} onClose={() => setModalOpen(false)}>
      <div className="text-sm">{modalMessage}</div>
      <div className="mt-4 flex justify-end">
        <button onClick={() => setModalOpen(false)} className="px-3 py-1 bg-blue-600 text-white rounded">OK</button>
      </div>
    </Modal>
    </>
  );
};

// Render validation modal
// Note: placed after the component to keep the JSX tidy — it will be included in the component render
// by virtue of being returned above (modal is local state-driven).


export default YouTubeGame;

YouTubeGame.Guide = function YouTubeGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">YouTube Watch Party — Guide</h3>
      <ul className="list-disc ml-6">
        <li>Hosts can control playback, suggest videos, and manage the queue.</li>
        <li>Viewers can watch the video and see the current queue.</li>
        <li>To suggest a video, enter a YouTube video ID or URL in the input box and click "Suggest".</li>
        <li>Hosts can play/pause the video, seek to different times, and play the next suggestion in the queue.</li>
        <li>The queue is shared among all viewers; when a host plays a suggestion, it will start for everyone.</li>
        <li>When a video ends, it will automatically advance to the next suggestion in the queue.</li>
        <li>You can drag and drop suggestions to reorder them in the queue (hosts only).</li>
      </ul>
    </div>
  );
};
